import { css } from 'lit';
/* eslint-disable max-lines */
import {
	ChangeSet,
	ChangeSetItem,
	EventStatus,
	LaneBlockage,
	LaneImpactValue,
	RespondingUnitMember,
	RoadEventTimelineDto,
	TimelineEntry,
	WorkZoneRequestStatus,
} from '@typings/api';
import {
	TimelineActivity,
	TimelineActivityCategory,
	TimelineActivityDay,
	TimelineActivityField,
	TimelineActivitySign,
	TimelineMoment,
} from '@typings/shared-types';
import { ConfigREMEventForm } from '@config/ConfigREM';
import { isValidString, numberIsValidAndFinite } from '@/utils/utils';
import {
	NoLaneImpact,
	formatChangeValue,
	formatTimelineDate,
	formatTimelineDateAndTime,
	formatTimelineTime,
	laneBlockageToText,
	parseDisplayedSignMessage,
	parseEditedSignMessage,
	sentenceCase,
} from './timeline-value-format';

/**	Source entries saved within this window merge change sets (burst saves of one entity).	*/
const MomentWindowMs = 1000;

/**	Reading order of activities inside one moment: what changed, then its consequences.	*/
const CategoryOrder: TimelineActivityCategory[] = [
	'event',
	'publish',
	'notes',
	'attributes',
	'vehicles',
	'units',
	'dispositions',
	'links',
	'email',
	'signs',
];

const fieldLabels: Record<string, string> = {
	routeDesignator: 'Route',
	mileStart: 'Start MP',
	mileEnd: 'End MP',
	county: 'County',
	district: 'District',
	subdistrict: 'Sub-district',
	unit: 'Unit',
	region: 'Region',
	city: 'City',
	crossStreetStart: 'Start Cross Street',
	crossStreetEnd: 'End Cross Street',
	proximity: 'Proximity',
	internalContactDetails: 'Internal contact details',
	additionalPublicText: 'Additional public text',
	additionalPublicAudio: 'Additional public audio',
	eastboundLaneBlockage: 'Eastbound',
	westboundLaneBlockage: 'Westbound',
	northboundLaneBlockage: 'Northbound',
	southboundLaneBlockage: 'Southbound',
	positiveLaneBlockage: 'Positive direction',
	negativeLaneBlockage: 'Negative direction',
	licensePlate: 'License plate',
	description: 'Description',
	respondingUnitType: 'Responding unit type',
	respondingUnitMember: 'Responding unit member',
	recordStatus: 'Status',
	assigned: 'Assigned',
	arrived: 'Arrived',
	completed: 'Completed',
	eventSource: 'Source',
	reporterName: 'Reporter',
	reporterPhoneNumber: 'Reporter phone',
	emailAddress: 'Email address',
	locationDescription: 'Location notes',
	dispositionType: 'Disposition type',
	Added: 'Added',
	Removed: 'Removed',
};

const LocationFieldOrder = [
	'routeDesignator',
	'mileStart',
	'mileEnd',
	'crossStreetStart',
	'crossStreetEnd',
	'proximity',
	'county',
	'district',
	'subdistrict',
	'unit',
	'region',
	'city',
];

const UnitFieldOrder = [
	'respondingUnitType',
	'respondingUnitMember',
	'assigned',
	'arrived',
	'completed',
	'recordStatus',
];

const LaneDirectionRegex = /^(eastbound|westbound|northbound|southbound|positive|negative)/;

/**	Contributes fields and a summary phrase to the change set's single activity row.	*/
type MergePart = {
	kind: 'merge';
	fields: TimelineActivityField[];
	phrases: string[];
};

/**	Emits a standalone activity row, used where one change set describes several actions.	*/
type RowPart = {
	kind: 'row';
	category?: TimelineActivityCategory;
	title: string;
	subtitle?: string;
	fields?: TimelineActivityField[];
	signs?: TimelineActivitySign[];
	messagePages?: string[];
	attributeChanges?: { action: 'Added' | 'Removed'; names: string[] }[];
	expandable?: boolean;
};

type ActivityPart = MergePart | RowPart;

type GroupContext = {
	timestamp: number;
	/**	True when this change set is the first revision of its entity.	*/
	created: boolean;
};

type ActivityGroup = {
	name: string;
	changeSetName: string;
	/**	Field names this group claims. An empty list claims every item in the change set.	*/
	fields: string[];
	category: TimelineActivityCategory;
	build: (items: ChangeSetItem[], context: GroupContext) => ActivityPart[];
};

type MatchedGroup = {
	group: ActivityGroup;
	items: ChangeSetItem[];
	parts: ActivityPart[];
};

const merge = (fields: TimelineActivityField[], phrases: string[] = []): MergePart[] => {
	const kept = phrases.filter(isValidString);

	if (fields.length === 0 && kept.length === 0) {
		return [];
	}

	return [{ kind: 'merge', fields, phrases: kept }];
};

/**
 * A field with neither a value nor a previous value says nothing. This is common on creation, where
 * the backend reports every untouched collection as an empty array.
 */
const isMeaningfulField = (field: TimelineActivityField): boolean =>
	field.current != null || field.previous != null;

/**	Builds a previous/current pair, marking a vanished value as cleared rather than blank.	*/
const toField = (label: string, item: ChangeSetItem): TimelineActivityField => {
	const fieldName = item.fieldName ?? '';
	const current = formatChangeValue(fieldName, item.value);
	const previous = formatChangeValue(fieldName, item.previousValue);

	return {
		label,
		current,
		previous: previous !== current ? previous : undefined,
		cleared: current == null,
	};
};

/**	Falls back to spacing out a camelCase field name so unmapped fields stay readable.	*/
const humanizeFieldName = (fieldName: string): string => {
	const spaced = fieldName.replace(/([a-z\d])([A-Z])/g, '$1 $2').toLowerCase();

	return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const labelFor = (item: ChangeSetItem): string => {
	const fieldName = item.fieldName ?? '';

	return (
		fieldLabels[fieldName] ?? (isValidString(fieldName) ? humanizeFieldName(fieldName) : 'Value')
	);
};

const sortByFieldOrder = (items: ChangeSetItem[], order: string[]): ChangeSetItem[] =>
	[...items].sort(
		(itemA, itemB) => order.indexOf(itemA.fieldName ?? '') - order.indexOf(itemB.fieldName ?? ''),
	);

const describeField = (field: TimelineActivityField): string => {
	if (field.cleared) {
		return `Cleared ${field.label.toLowerCase()}`;
	}

	return `${field.label} ${field.current ?? ''}`.trim();
};

/**	Drops fields that carry no information, then summarises whatever is left.	*/
const mergeFields = (
	fields: TimelineActivityField[],
	describe: (field: TimelineActivityField) => string = describeField,
): MergePart[] => {
	const kept = fields.filter(isMeaningfulField);

	return merge(kept, kept.map(describe));
};

const uniqueSigns = (signs: TimelineActivitySign[]): TimelineActivitySign[] => {
	const byName = new Map<string, TimelineActivitySign>();

	signs.forEach((sign) => {
		const existing = byName.get(sign.name);

		byName.set(sign.name, existing ? { ...existing, ...sign } : sign);
	});

	return [...byName.values()];
};

const countSigns = (signs: TimelineActivitySign[], noun = 'sign'): string =>
	signs.length === 1 ? `1 ${noun}` : `${signs.length} ${noun}s`;

const activityGroups: ActivityGroup[] = [
	{
		name: 'Publish Status',
		changeSetName: 'event',
		fields: ['publishStatus'],
		category: 'publish',
		build: (items) => {
			const statusKey = items[0].value as keyof typeof WorkZoneRequestStatus;
			const status = WorkZoneRequestStatus[statusKey];
			const previousKey = items[0].previousValue as keyof typeof WorkZoneRequestStatus;
			const previous = WorkZoneRequestStatus[previousKey];

			if (!isValidString(status)) {
				return [];
			}

			return merge(
				[
					{
						label: 'Publish status',
						current: status,
						previous: isValidString(previous) ? previous : undefined,
						renderAs: 'publish-status',
					},
				],
				[sentenceCase(status)],
			);
		},
	},
	{
		name: 'Rejection Reason',
		changeSetName: 'event',
		fields: ['publishNotes'],
		category: 'publish',
		build: (items) => {
			const reason = formatChangeValue('publishNotes', items[0].value);

			if (reason == null) {
				return [];
			}

			return merge(
				[{ label: 'Rejection reason', current: reason, highlighted: true }],
				['Rejected'],
			);
		},
	},
	{
		name: 'Scheduling',
		changeSetName: 'event',
		fields: ['eventStart', 'eventEnd'],
		category: 'event',
		build: (items) => {
			const fields: TimelineActivityField[] = [];
			const phrases: string[] = [];

			items.forEach((item) => {
				const time = numberIsValidAndFinite(item.value) ? item.value : NaN;
				let label = 'Scheduled end';

				if (item.fieldName === 'eventStart') {
					label = time < Date.now() ? 'Event started' : 'Scheduled start';
				}

				fields.push(toField(label, item));
				phrases.push(Number.isNaN(time) ? `${label} removed` : label);
			});

			return merge(fields, phrases);
		},
	},
	{
		name: 'Event Status',
		changeSetName: 'event',
		fields: ['eventStatus'],
		category: 'event',
		build: (items, context) => {
			const previousStatus = items[0].previousValue as EventStatus;
			const status = items[0].value as EventStatus;
			let phrase: string | null = null;

			if (status === EventStatus.COMPLETED) {
				phrase = 'Event closed';
			} else if (status === EventStatus.NORMAL && previousStatus === EventStatus.COMPLETED) {
				phrase = 'Event reopened';
			}

			if (phrase == null) {
				return [];
			}

			return merge(
				[{ label: phrase, current: formatTimelineDateAndTime(context.timestamp) }],
				[phrase],
			);
		},
	},
	{
		name: 'Headline',
		changeSetName: 'event',
		fields: ['eventType'],
		category: 'event',
		build: (items) => {
			const fields = items.map((item) => {
				const field = toField('Headline', item);

				return {
					...field,
					current: field.current != null ? sentenceCase(field.current) : undefined,
					previous: field.previous != null ? sentenceCase(field.previous) : undefined,
				};
			});

			return merge(
				fields,
				fields.map((field) => field.current ?? 'Headline removed'),
			);
		},
	},
	{
		name: 'Source',
		changeSetName: 'event',
		fields: ['eventSource', 'reporterName', 'reporterPhoneNumber'],
		category: 'event',
		build: (items) => {
			const fields = items.map((item) => toField(labelFor(item), item));

			return mergeFields(fields);
		},
	},
	{
		name: 'Display on 511',
		changeSetName: 'event',
		fields: ['includeInEventFeed'],
		category: 'publish',
		build: (items) => {
			const included = items[0].value === true;
			const phrase = included ? 'Sent to 511' : 'Excluded from 511';

			return merge([{ label: 'Display on 511', current: phrase }], [phrase]);
		},
	},
	{
		name: 'Location',
		changeSetName: 'event',
		fields: LocationFieldOrder,
		category: 'event',
		build: (items) => {
			const fields = sortByFieldOrder(items, LocationFieldOrder).map((item) =>
				toField(labelFor(item), item),
			);

			return mergeFields(fields);
		},
	},
	{
		name: 'Lane impacts',
		changeSetName: 'event',
		fields: [
			'eastboundLaneBlockageType',
			'westboundLaneBlockageType',
			'northboundLaneBlockageType',
			'southboundLaneBlockageType',
			'positiveLaneBlockageType',
			'negativeLaneBlockageType',
			'eastboundLaneBlockage',
			'westboundLaneBlockage',
			'northboundLaneBlockage',
			'southboundLaneBlockage',
			'positiveLaneBlockage',
			'negativeLaneBlockage',
		],
		category: 'event',
		build: (items) => {
			const byDirection = new Map<string, { type?: ChangeSetItem; blockage?: ChangeSetItem }>();

			items.forEach((item) => {
				const direction = LaneDirectionRegex.exec(item.fieldName ?? '')?.[1];

				if (direction == null) {
					return;
				}
				const entry = byDirection.get(direction) ?? {};

				if (item.fieldName?.includes('Type')) {
					entry.type = item;
				} else {
					entry.blockage = item;
				}
				byDirection.set(direction, entry);
			});

			const fields: TimelineActivityField[] = [];

			const typeSuffix = (type?: LaneImpactValue): string =>
				type != null && type !== LaneImpactValue.NA ? ` (${type})` : '';

			byDirection.forEach((pair, direction) => {
				const impactType = pair.type?.value as LaneImpactValue | undefined;
				const previousImpactType = pair.type?.previousValue as LaneImpactValue | undefined;
				let current: string | undefined;
				let previous: string | undefined;

				if (pair.blockage != null) {
					const impact = laneBlockageToText(
						pair.blockage.value as LaneBlockage,
						pair.blockage.previousValue as LaneBlockage,
					);

					current = `${impact}${typeSuffix(impactType)}`;
					if (pair.blockage.previousValue != null) {
						previous = `${laneBlockageToText(
							pair.blockage.previousValue as LaneBlockage,
						)}${typeSuffix(previousImpactType)}`;
					} else if (previousImpactType != null && previousImpactType !== LaneImpactValue.NA) {
						previous = previousImpactType;
					}
				} else if (pair.type != null) {
					//	type-only change: do not invent "No lanes impacted" for a missing blockage payload
					current =
						impactType != null && impactType !== LaneImpactValue.NA ? impactType : undefined;
					previous =
						previousImpactType != null && previousImpactType !== LaneImpactValue.NA
							? previousImpactType
							: undefined;
				}

				if (current == null && previous == null) {
					return;
				}

				fields.push({
					label: fieldLabels[`${direction}LaneBlockage`] ?? humanizeFieldName(direction),
					current,
					previous: previous !== current ? previous : undefined,
				});
			});

			//	an unimpacted direction that was already unimpacted is not worth summarising
			const summarised = fields.filter(
				(field) => field.current !== NoLaneImpact || field.previous != null,
			);

			return merge(
				summarised,
				summarised.map((field) => `${field.label} ${field.current?.toLowerCase() ?? ''}`.trim()),
			);
		},
	},
	{
		name: 'Location Notes',
		changeSetName: 'event',
		fields: ['locationDescription'],
		category: 'notes',
		build: (items) => {
			const fields = items.map((item) => ({
				...toField('Location notes', item),
				highlighted: true,
			}));

			return mergeFields(fields);
		},
	},
	{
		name: 'Notes',
		changeSetName: 'event',
		fields: ['internalContactDetails', 'additionalPublicText', 'additionalPublicAudio'],
		category: 'notes',
		build: (items) => {
			const fields = items.map((item) => ({
				...toField(labelFor(item), item),
				highlighted: true,
			}));

			return mergeFields(fields);
		},
	},
	{
		name: 'Visual Verification',
		changeSetName: 'event',
		fields: ['verified'],
		category: 'event',
		build: (items) => {
			const item = items[0];
			const verifiedAt = formatChangeValue('verified', item.value);
			const by = isValidString(item.createdBy) ? ` by ${item.createdBy}` : '';

			return merge(
				[
					{
						label: 'Visual verification',
						current: verifiedAt != null ? `Verified on ${verifiedAt}${by}` : `Unverified${by}`,
					},
				],
				[verifiedAt != null ? 'Verified' : 'Unverified'],
			);
		},
	},
	{
		name: 'Priority',
		changeSetName: 'event',
		fields: ['priority'],
		category: 'event',
		build: (items) => {
			const field = toField('Priority', items[0]);

			return merge([field], [`Priority ${field.current ?? 'removed'}`]);
		},
	},
	{
		name: 'Email Notification Message',
		changeSetName: 'event',
		fields: ['text'],
		category: 'email',
		build: (items) => {
			const field = toField('Email message', items[0]);

			return merge([field], ['Email message updated']);
		},
	},
	{
		name: 'Message Displayed on Signs',
		changeSetName: 'signs-display',
		fields: [],
		category: 'signs',
		build: (items) => {
			const byMessage = new Map<string, { pages: string[]; signs: TimelineActivitySign[] }>();

			items.forEach((item) => {
				const { action, pages } = parseDisplayedSignMessage(item.value);

				if (action !== 'Added' || pages.length === 0 || !isValidString(item.fieldName)) {
					return;
				}
				const key = pages.join('\n');
				const entry = byMessage.get(key) ?? { pages, signs: [] };

				entry.signs.push({ name: item.fieldName, displayed: true });
				byMessage.set(key, entry);
			});

			return [...byMessage.values()].map((entry) => {
				const signs = uniqueSigns(entry.signs);

				return {
					kind: 'row' as const,
					title: `Message displayed on ${countSigns(signs)}`,
					signs,
					messagePages: entry.pages,
					expandable: false,
				};
			});
		},
	},
	{
		name: 'Message Removed from Signs',
		changeSetName: 'signs-display',
		fields: [],
		category: 'signs',
		build: (items) => {
			const signs = uniqueSigns(
				items
					.filter(
						(item) =>
							parseDisplayedSignMessage(item.value).action === 'Removed' &&
							isValidString(item.fieldName),
					)
					.map((item) => ({ name: item.fieldName as string, removed: true })),
			);

			if (signs.length === 0) {
				return [];
			}

			return [
				{
					kind: 'row' as const,
					title: `Message removed from ${countSigns(signs)}`,
					signs,
					expandable: false,
				},
			];
		},
	},
	{
		name: 'Sign Update',
		changeSetName: 'signs',
		fields: ['Added', 'Removed'],
		category: 'signs',
		build: (items) => {
			const added = uniqueSigns(
				items
					.filter((item) => item.fieldName === 'Added' && isValidString(item.value))
					.map((item) => ({ name: item.value as string })),
			);
			const removed = uniqueSigns(
				items
					.filter((item) => item.fieldName === 'Removed' && isValidString(item.value))
					.map((item) => ({ name: item.value as string, removed: true })),
			);
			const rows: RowPart[] = [];

			if (added.length > 0) {
				rows.push({
					kind: 'row',
					title: `Added to ${countSigns(added, 'sign queue')}`,
					signs: added,
					expandable: true,
				});
			}
			if (removed.length > 0) {
				rows.push({
					kind: 'row',
					title: `Removed from ${countSigns(removed, 'sign queue')}`,
					signs: removed,
					expandable: true,
				});
			}

			return rows;
		},
	},
	{
		name: 'Sign Message Edited',
		changeSetName: 'messages',
		fields: ['Added', 'Removed'],
		category: 'signs',
		build: (items) =>
			items.flatMap((item) => {
				const { signName, pages } = parseEditedSignMessage(item.value);

				if (pages.length === 0) {
					return [];
				}

				return [
					{
						kind: 'row' as const,
						title: `Message edited${signName != null ? ` on ${signName}` : ''}`,
						signs: signName != null ? [{ name: signName }] : undefined,
						messagePages: pages,
						expandable: false,
					},
				];
			}),
	},
	{
		name: 'Vehicles',
		changeSetName: 'vehicles',
		fields: ['licensePlate', 'description'],
		category: 'vehicles',
		build: (items) => {
			const fields = items.map((item) => {
				const field = toField(labelFor(item), item);

				if (item.fieldName !== 'licensePlate') {
					return field;
				}

				return {
					...field,
					current: field.current?.replace(
						ConfigREMEventForm.vehicles.licensePlateNumberDelimitter,
						' - ',
					),
				};
			});

			return mergeFields(fields);
		},
	},
	{
		name: 'Responding Units',
		changeSetName: 'respondingUnits',
		fields: UnitFieldOrder,
		category: 'units',
		build: (items) => {
			const fields = sortByFieldOrder(items, UnitFieldOrder).map((item) => {
				if (item.fieldName !== 'respondingUnitMember') {
					return toField(labelFor(item), item);
				}
				const member = item.value as RespondingUnitMember | undefined;
				const previousMember = item.previousValue as RespondingUnitMember | undefined;
				const current = isValidString(member?.name) ? member.name : undefined;
				const previous = isValidString(previousMember?.name) ? previousMember.name : undefined;

				return {
					label: labelFor(item),
					current,
					previous: previous !== current ? previous : undefined,
					cleared: current == null,
				};
			});

			//	the unit's type and crew name identify it, so lead with those and skip the record status
			return mergeFields(fields, (field) => {
				if (field.label === fieldLabels.recordStatus) {
					return '';
				}
				if (
					field.label === fieldLabels.respondingUnitType ||
					field.label === fieldLabels.respondingUnitMember
				) {
					return field.cleared ? `Cleared ${field.label.toLowerCase()}` : (field.current ?? '');
				}

				return describeField(field);
			});
		},
	},
	{
		name: 'Dispositions',
		changeSetName: 'respondingUnitDispositions',
		fields: ['dispositionType'],
		category: 'dispositions',
		build: (items) => {
			const fields = items.map((item) => toField('Disposition type', item));

			return merge(
				fields,
				fields.map((field) =>
					field.cleared ? 'Disposition removed' : `Disposition ${field.current ?? ''}`.trim(),
				),
			);
		},
	},
	{
		name: 'Attributes',
		changeSetName: 'attributes',
		fields: ['value'],
		category: 'attributes',
		build: (items) => {
			const added: string[] = [];
			const removed: string[] = [];

			items.forEach((item) => {
				if (isValidString(item.value)) {
					added.push(item.value);
				} else if (isValidString(item.previousValue)) {
					removed.push(item.previousValue);
				}
			});

			const attributeChanges: { action: 'Added' | 'Removed'; names: string[] }[] = [];

			if (added.length > 0) {
				attributeChanges.push({ action: 'Added', names: added });
			}
			if (removed.length > 0) {
				attributeChanges.push({ action: 'Removed', names: removed });
			}
			if (attributeChanges.length === 0) {
				return [];
			}

			return [
				{
					kind: 'row' as const,
					title: 'Attribute Update',
					//	tags render inline; a subtitle would only repeat them
					attributeChanges,
					expandable: false,
				},
			];
		},
	},
	{
		name: 'Linked events',
		changeSetName: 'eventLinkages',
		fields: ['linkedEventB'],
		category: 'links',
		build: (items) => {
			const linked = items
				.map((item) => item.value)
				.filter(numberIsValidAndFinite)
				.map((id) => `#${id}`);
			const unlinked = items
				.filter(
					(item) =>
						!numberIsValidAndFinite(item.value) && numberIsValidAndFinite(item.previousValue),
				)
				.map((item) => item.previousValue)
				.filter(numberIsValidAndFinite)
				.map((id) => `#${id}`);

			if (linked.length === 0 && unlinked.length === 0) {
				return [];
			}

			const fields: TimelineActivityField[] = [];
			const phrases: string[] = [];

			if (linked.length > 0) {
				fields.push({ label: 'Linked events', current: linked.join(', ') });
				phrases.push(`Linked ${linked.join(', ')}`);
			}
			if (unlinked.length > 0) {
				fields.push({
					label: 'Linked events',
					previous: unlinked.join(', '),
					cleared: true,
				});
				phrases.push(`Unlinked ${unlinked.join(', ')}`);
			}

			return merge(fields, phrases);
		},
	},
	{
		name: 'Email Notification Recipients',
		changeSetName: 'emailRecipients',
		fields: ['emailAddress', 'Added', 'Removed'],
		category: 'email',
		build: (items) => {
			const fields = items.map((item) => toField(labelFor(item), item));

			return merge(fields, ['Recipients updated']);
		},
	},
	{
		name: 'Email Notification Groups',
		changeSetName: 'emailGroups',
		fields: ['Added', 'Removed'],
		category: 'email',
		build: (items) => {
			const fields = items.map((item) => toField(labelFor(item), item));

			return merge(fields, ['Groups updated']);
		},
	},
];

/**	Group names surfaced by the Sign Updates tab.	*/
export const SignUpdateGroupNames = [
	'Message Displayed on Signs',
	'Message Removed from Signs',
	'Sign Update',
	'Sign Message Edited',
];

const groupNameOrder = activityGroups.map((group) => group.name);

function matchGroups(changeSet: ChangeSet, context: GroupContext): MatchedGroup[] {
	const items = changeSet.changeSetItems ?? [];
	const matched: MatchedGroup[] = [];

	activityGroups.forEach((group) => {
		if (group.changeSetName !== (changeSet.name ?? '')) {
			return;
		}

		const groupItems = items.filter(
			(item) =>
				group.fields.length === 0 ||
				(isValidString(item.fieldName) && group.fields.includes(item.fieldName)),
		);

		if (groupItems.length === 0) {
			return;
		}

		const parts = group.build(groupItems, context);

		if (parts.length > 0) {
			matched.push({ group, items: groupItems, parts });
		}
	});

	return matched;
}

/**	Picks the category that best describes a change set from the groups that matched it.	*/
function primaryCategory(categories: TimelineActivityCategory[]): TimelineActivityCategory {
	return CategoryOrder.find((category) => categories.includes(category)) ?? 'event';
}

function eventCreatedSubtitle(matched: MatchedGroup[], phrases: string[]): string {
	const headline = matched
		.find((entry) => entry.group.name === 'Headline')
		?.items.map((item) => formatChangeValue('eventType', item.value))
		.find(isValidString);
	const route = matched
		.find((entry) => entry.group.name === 'Location')
		?.items.find((item) => item.fieldName === 'routeDesignator');
	const routeName = formatChangeValue('routeDesignator', route?.value);
	const parts: string[] = [];

	if (isValidString(headline)) {
		parts.push(sentenceCase(headline));
	}
	if (isValidString(routeName)) {
		parts.push(`${parts.length > 0 ? 'on ' : ''}${routeName}`);
	}

	return parts.length > 0 ? parts.join(' ') : phrases.join(' · ');
}

const ChangeSetTitles: Record<string, { created: string; updated: string }> = {
	respondingUnits: { created: 'Responding Unit Added', updated: 'Responding Unit Update' },
	respondingUnitDispositions: { created: 'Disposition Added', updated: 'Disposition Update' },
	vehicles: { created: 'Vehicle Added', updated: 'Vehicle Update' },
	eventLinkages: { created: 'Events Linked', updated: 'Linked Events Update' },
	emailRecipients: { created: 'Email Recipients Added', updated: 'Email Recipients Update' },
	emailGroups: { created: 'Email Groups Added', updated: 'Email Groups Update' },
};

function mergedRowTitle(
	changeSetName: string,
	matched: MatchedGroup[],
	phrases: string[],
	context: GroupContext,
): { title: string; subtitle?: string } {
	if (changeSetName === 'event') {
		if (context.created) {
			return { title: 'Event Created', subtitle: eventCreatedSubtitle(matched, phrases) };
		}

		const categories = matched.map((entry) => entry.group.category);
		const onlyPublish = categories.every((category) => category === 'publish');
		const onlyNotes = categories.every((category) => category === 'notes');
		const title =
			(onlyPublish && '511 Publishing Update') ||
			(onlyNotes && 'Notes Update') ||
			'Event Details Update';

		return { title, subtitle: phrases.join(' · ') };
	}

	const titles = ChangeSetTitles[changeSetName];
	const title = titles ? titles[context.created ? 'created' : 'updated'] : 'Update';

	return { title, subtitle: phrases.join(' · ') };
}

/**
 * Turns one change set into the activity rows it describes: a single merged row for the entity's
 * own fields, plus any standalone rows (signs, attributes) the change set also carries.
 */
function changeSetToActivities(
	changeSet: ChangeSet,
	user: string,
	timestamp: number,
): Omit<TimelineActivity, 'key' | 'searchText'>[] {
	const context: GroupContext = {
		timestamp,
		created: (changeSet.revisionNumber ?? 1) <= 1,
	};
	const matched = matchGroups(changeSet, context);

	if (matched.length === 0) {
		return [];
	}

	const mergeParts = matched.filter((entry) => entry.parts.some((part) => part.kind === 'merge'));
	const fields = mergeParts
		.flatMap((entry) => entry.parts.flatMap((part) => (part.kind === 'merge' ? part.fields : [])))
		.filter(isMeaningfulField);
	const phrases = mergeParts.flatMap((entry) =>
		entry.parts.flatMap((part) => (part.kind === 'merge' ? part.phrases : [])),
	);
	const activities: Omit<TimelineActivity, 'key' | 'searchText'>[] = [];

	if (fields.length > 0 || phrases.length > 0) {
		const { title, subtitle } = mergedRowTitle(changeSet.name ?? '', mergeParts, phrases, context);

		activities.push({
			category: primaryCategory(mergeParts.map((entry) => entry.group.category)),
			groupNames: mergeParts.map((entry) => entry.group.name),
			title,
			subtitle: isValidString(subtitle) ? subtitle : undefined,
			user,
			timestamp,
			fields,
			expandable: fields.length > 0,
		});
	}

	matched.forEach((entry) => {
		entry.parts.forEach((part) => {
			if (part.kind !== 'row') {
				return;
			}

			activities.push({
				category: part.category ?? entry.group.category,
				groupNames: [entry.group.name],
				title: part.title,
				subtitle: part.subtitle,
				user,
				timestamp,
				fields: part.fields ?? [],
				signs: part.signs,
				messagePages: part.messagePages,
				attributeChanges: part.attributeChanges,
				expandable: part.expandable ?? (part.fields?.length ?? 0) > 0,
			});
		});
	});

	return activities;
}

function getUserFromChangeSet(changeSet: ChangeSet): string {
	const userItem = changeSet.changeSetItems?.find((item) => isValidString(item.createdBy));

	return userItem?.createdBy ?? changeSet.name ?? 'Guest User';
}

/**
 * Collapses source entries saved within one second into a single moment bucket and merges change
 * sets that share a name and id (burst sign-queue adds). Wider same-minute grouping happens later
 * in coalesceMomentsByMinute so distinct revisions minutes apart stay separate activities.
 */
export function groupChangeSetsByTimestamp(
	entries: TimelineEntry[],
	thresholdMs: number = MomentWindowMs,
): TimelineEntry[] {
	if (entries.length === 0) {
		return [];
	}

	const result: TimelineEntry[] = [];
	const changeSetMap = new Map<string, ChangeSet>();
	let groupTimestamp = entries[0].timestamp;

	const flush = () => {
		result.push({ timestamp: groupTimestamp, changeSets: [...changeSetMap.values()] });
		changeSetMap.clear();
	};

	entries.forEach((entry, index) => {
		const timeDiff = index > 0 ? Math.abs(entry.timestamp - entries[index - 1].timestamp) : 0;

		if (timeDiff > thresholdMs) {
			flush();
			groupTimestamp = entry.timestamp;
		}

		entry.changeSets?.forEach((changeSet) => {
			const key = `${changeSet.name ?? ''}#${changeSet.id ?? ''}`;
			const existing = changeSetMap.get(key);

			if (existing) {
				existing.changeSetItems = [
					...(existing.changeSetItems ?? []),
					...(changeSet.changeSetItems ?? []),
				];
				const revisions = [existing.revisionNumber, changeSet.revisionNumber].filter(
					numberIsValidAndFinite,
				);

				//	leave undefined when both omit it so creations still title as Created/Added
				existing.revisionNumber = revisions.length > 0 ? Math.min(...revisions) : undefined;

				return;
			}

			changeSetMap.set(key, {
				...changeSet,
				changeSetItems: [...(changeSet.changeSetItems ?? [])],
			});
		});

		if (index === entries.length - 1) {
			flush();
		}
	});

	return result;
}

const activitySearchText = (
	activity: Omit<TimelineActivity, 'key' | 'searchText'>,
	dateString: string,
	timeString: string,
): string =>
	[
		activity.title,
		activity.subtitle,
		activity.user,
		dateString,
		timeString,
		...activity.fields.flatMap((field) => [field.label, field.current, field.previous]),
		...(activity.signs ?? []).map((sign) => sign.name),
		...(activity.messagePages ?? []),
		...(activity.attributeChanges ?? []).flatMap(({ action, names }) => [action, ...names]),
	]
		.filter(isValidString)
		.join(' ')
		.toLowerCase();

const sortActivitiesInMoment = (
	activities: Omit<TimelineActivity, 'key' | 'searchText'>[],
	timestamp: number,
	dateString: string,
	timeString: string,
): TimelineActivity[] =>
	[...activities]
		.sort((a, b) => {
			const byCategory = CategoryOrder.indexOf(a.category) - CategoryOrder.indexOf(b.category);

			if (byCategory !== 0) {
				return byCategory;
			}

			const byGroup =
				groupNameOrder.indexOf(a.groupNames[0]) - groupNameOrder.indexOf(b.groupNames[0]);

			if (byGroup !== 0) {
				return byGroup;
			}

			//	newer rows first when category and group match (e.g. queue add then remove)
			return b.timestamp - a.timestamp;
		})
		.map((activity, index) => ({
			...activity,
			key: `${timestamp}-${index}`,
			searchText: activitySearchText(activity, dateString, timeString),
		}));

/**
 * Merges moments that share a displayed clock time (hour+minute) so the list never shows multiple
 * headings for e.g. "02:44 PM". Activities stay distinct; only the heading is shared.
 */
function coalesceMomentsByMinute(days: TimelineActivityDay[]): TimelineActivityDay[] {
	return days.map((day) => {
		const coalesced: TimelineMoment[] = [];
		const byTime = new Map<string, TimelineMoment>();

		day.moments.forEach((moment) => {
			const existing = byTime.get(moment.timeString);

			if (existing == null) {
				const copy: TimelineMoment = {
					...moment,
					activities: [...moment.activities],
					users: [...moment.users],
				};

				byTime.set(moment.timeString, copy);
				coalesced.push(copy);

				return;
			}

			existing.activities = sortActivitiesInMoment(
				[...existing.activities, ...moment.activities],
				existing.timestamp,
				day.dateString,
				existing.timeString,
			);
			existing.users = [...new Set(existing.activities.map((activity) => activity.user))];
		});

		return {
			...day,
			moments: coalesced,
			activityCount: coalesced.reduce((total, moment) => total + moment.activities.length, 0),
		};
	});
}

/**
 * Transforms a raw event timeline into the day → moment → activity shape the timeline panel
 * renders. Days and moments are newest first; activities inside a moment read cause before effect.
 */
export function processTimelineToActivityDays(
	timeline?: RoadEventTimelineDto['timeline'],
): TimelineActivityDay[] {
	const entries = (timeline?.entries ?? []).filter((entry): entry is TimelineEntry =>
		numberIsValidAndFinite(entry.timestamp),
	);
	const dayMap = new Map<string, TimelineActivityDay>();

	groupChangeSetsByTimestamp(entries).forEach((entry) => {
		const { timestamp } = entry;
		const dateString = formatTimelineDate(timestamp);
		const timeString = formatTimelineTime(timestamp);
		const activities = (entry.changeSets ?? []).flatMap((changeSet) =>
			changeSetToActivities(changeSet, getUserFromChangeSet(changeSet), timestamp),
		);

		if (activities.length === 0) {
			return;
		}

		const moment: TimelineMoment = {
			key: `${timestamp}`,
			timestamp,
			timeString,
			users: [...new Set(activities.map((activity) => activity.user))],
			activities: sortActivitiesInMoment(activities, timestamp, dateString, timeString),
		};

		const day = dayMap.get(dateString) ?? {
			key: dateString,
			dateString,
			timestamp,
			activityCount: 0,
			moments: [],
		};

		day.moments.push(moment);
		day.activityCount += moment.activities.length;
		day.timestamp = Math.max(day.timestamp, timestamp);
		dayMap.set(dateString, day);
	});

	return coalesceMomentsByMinute(
		[...dayMap.values()]
			.map((day) => ({
				...day,
				moments: [...day.moments].sort((a, b) => b.timestamp - a.timestamp),
			}))
			.sort((a, b) => b.timestamp - a.timestamp),
	);
}

/**	Rebuilds days and moments from a per-activity map that may drop or rewrite rows.	*/
function mapActivityDays(
	days: TimelineActivityDay[],
	mapActivity: (
		activity: TimelineActivity,
		day: TimelineActivityDay,
		moment: TimelineMoment,
	) => TimelineActivity | null,
): TimelineActivityDay[] {
	return days
		.map((day) => {
			const moments = day.moments
				.map((moment) => {
					const activities = moment.activities
						.map((activity) => mapActivity(activity, day, moment))
						.filter((activity): activity is TimelineActivity => activity != null);

					return {
						...moment,
						activities,
						//	the author line depends on who is still visible after filtering
						users: [...new Set(activities.map((activity) => activity.user))],
					};
				})
				.filter((moment) => moment.activities.length > 0);

			return {
				...day,
				moments,
				activityCount: moments.reduce((total, moment) => total + moment.activities.length, 0),
			};
		})
		.filter((day) => day.moments.length > 0);
}

/**	Rebuilds days and moments around the activities that survive a predicate, dropping empties.	*/
function filterActivityDaysBy(
	days: TimelineActivityDay[],
	predicate: (activity: TimelineActivity) => boolean,
): TimelineActivityDay[] {
	return mapActivityDays(days, (activity) => (predicate(activity) ? activity : null));
}

/**	The Sign Updates tab is a filtered view of the same transformed timeline.	*/
export function filterActivityDaysToSignUpdates(
	days: TimelineActivityDay[],
): TimelineActivityDay[] {
	return filterActivityDaysBy(days, (activity) =>
		activity.groupNames.some((name) => SignUpdateGroupNames.includes(name)),
	);
}

/**	Sentinel for the date tile that clears the date filter.	*/
export const AllDates = 'all';

const textMatches = (value: string | undefined, needle: string): boolean =>
	value != null && value.toLowerCase().includes(needle);

const fieldMatches = (field: TimelineActivityField, needle: string): boolean =>
	textMatches(field.label, needle) ||
	textMatches(field.current, needle) ||
	textMatches(field.previous, needle);

/**	Recounts sign-activity titles after the chip list has been narrowed by search.	*/
const recountSignTitle = (title: string, signs: TimelineActivitySign[]): string => {
	if (/^Added to \d+ sign queues?$/.test(title)) {
		return `Added to ${countSigns(signs, 'sign queue')}`;
	}
	if (/^Removed from \d+ sign queues?$/.test(title)) {
		return `Removed from ${countSigns(signs, 'sign queue')}`;
	}
	if (/^Message displayed on \d+ signs?$/.test(title)) {
		return `Message displayed on ${countSigns(signs)}`;
	}
	if (/^Message removed from \d+ signs?$/.test(title)) {
		return `Message removed from ${countSigns(signs)}`;
	}

	return title;
};

/**
 * Keeps an activity when the query hits its metadata or any detail, and when the hit is inside the
 * details trims the row to just the matching fields / signs / attributes / message pages.
 */
function narrowActivityToSearch(
	activity: TimelineActivity,
	needle: string,
	dateOrTimeHit: boolean,
): TimelineActivity | null {
	const metaHit =
		dateOrTimeHit ||
		textMatches(activity.title, needle) ||
		textMatches(activity.subtitle, needle) ||
		textMatches(activity.user, needle);

	const matchingFields = activity.fields.filter((field) => fieldMatches(field, needle));
	const matchingSigns = (activity.signs ?? []).filter((sign) => textMatches(sign.name, needle));
	const matchingPages = (activity.messagePages ?? []).filter((page) => textMatches(page, needle));
	const matchingAttrs = (activity.attributeChanges ?? [])
		.map(({ action, names }) => ({
			action,
			names: textMatches(action, needle)
				? names
				: names.filter((name) => textMatches(name, needle)),
		}))
		.filter(({ names }) => names.length > 0);

	const detailHit =
		matchingFields.length > 0 ||
		matchingSigns.length > 0 ||
		matchingPages.length > 0 ||
		matchingAttrs.length > 0;

	if (!metaHit && !detailHit) {
		return null;
	}

	//	metadata-only hit (title, author, date, time): keep the whole activity
	if (!detailHit) {
		return activity;
	}

	let signs: TimelineActivity['signs'];

	if (matchingSigns.length > 0) {
		signs = matchingSigns;
	} else if (matchingPages.length > 0) {
		//	a message-text hit still needs the boards that showed it
		signs = activity.signs;
	}

	return {
		...activity,
		title:
			matchingSigns.length > 0 ? recountSignTitle(activity.title, matchingSigns) : activity.title,
		//	the subtitle summarised the full set; drop it once we are showing a subset
		subtitle: matchingFields.length > 0 ? undefined : activity.subtitle,
		fields: matchingFields,
		signs,
		messagePages: matchingPages.length > 0 ? matchingPages : undefined,
		attributeChanges: matchingAttrs.length > 0 ? matchingAttrs : undefined,
		//	surface the matching details without requiring a click
		expandable: false,
	};
}

/**
 * Narrows the timeline by the search box and the selected date tile. Search covers the row title,
 * summary, author, date, time, field names, and both previous and current values — and when the
 * hit is a field, sign, attribute, or message page, only that detail is kept on the row.
 */
export function filterActivityDays(
	days: TimelineActivityDay[],
	query = '',
	dateString: string = AllDates,
): TimelineActivityDay[] {
	const needle = query.trim().toLowerCase();

	if (needle === '' && dateString === AllDates) {
		return days;
	}

	const byDate = days.filter((day) => dateString === AllDates || day.dateString === dateString);

	if (needle === '') {
		return byDate;
	}

	return mapActivityDays(byDate, (activity, day, moment) =>
		narrowActivityToSearch(
			activity,
			needle,
			textMatches(day.dateString, needle) || textMatches(moment.timeString, needle),
		),
	);
}

export function processSignUpdatesToActivityDays(
	timeline?: RoadEventTimelineDto['timeline'],
): TimelineActivityDay[] {
	return filterActivityDaysToSignUpdates(processTimelineToActivityDays(timeline));
}
